/* ENZOS – Cart Drawer Recos (no-rec-when-empty, fallback only as top-up, dedupe, bulletproof) */
(() => {
  const DEBUG = false;
  const log = (...a) => DEBUG && console.log('[DD recos]', ...a);

  const WRAP_ID = 'dd-cart-drawer-recos';
  const GRID_SELECTOR = '[data-grid]';
  const STATUS_SEL = '.dd-rec__status';
  const FALLBACK_COLLECTION = 'cart-upsells';
  const RECO_LIMIT = 4;

  // Only use fallback when cart has items and recos < limit
  const USE_FALLBACK_WHEN_CART_EMPTY = false;
  const TOP_UP_FROM_COLLECTION = true;

  // HTML we can re-insert if theme replaces drawer content
  const MOUNT_HTML = `
    <div id="dd-cart-drawer-recos"
         data-fallback-handle="cart-upsells"
         class="dd-rec dd-rec--pending">
      <h4 class="dd-rec__heading">TOP IT OFF</h4>
      <div class="dd-rec__grid" data-grid>
        <div class="dd-rec__status">Loading…</div>
      </div>
    </div>`;

  // ---------------- utils ----------------
  const money = (cents) => {
    try { return Shopify.formatMoney(cents, Shopify.money_format || "${{amount}}"); }
    catch { return '$' + (Number(cents) / 100).toFixed(2); }
  };
  const sized = (src, w) => {
    if (!src) return '';
    if (src.startsWith('//')) src = 'https:' + src;
    const sep = src.includes('?') ? '&' : '?';
    return `${src}${sep}width=${w}`;
  };
  const pickImg = (p) => {
    if (p?.featured_image?.url) return p.featured_image.url;
    if (typeof p?.featured_image === 'string') return p.featured_image;
    if (typeof p?.image === 'string') return p.image;
    if (Array.isArray(p?.images) && p.images.length) {
      const f = p.images[0]; return f?.url || f?.src || '';
    }
    return '';
  };
  const productUrl = (p) => p?.url || (p?.handle ? `/products/${p.handle}` : '#');
  const firstVariant = (p) => Array.isArray(p?.variants)
    ? (p.variants.find(v => v?.available || v?.inventory_quantity > 0 || v?.id) || p.variants[0])
    : null;

  const dedupeByHandleOrId = (arr) => {
    const seen = new Set();
    return arr.filter(p => {
      const key = p.handle || p.id || (p.variants && p.variants[0]?.product_id) || Math.random();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // Ensure mount exists; recreate if missing
  function ensureMount() {
    let wrap = document.getElementById(WRAP_ID);
    if (wrap) return wrap;

    const main = document.querySelector('#sidebar-cart .Drawer__Main');
    if (!main) return null;

    // Prefer inserting after the items container
    const after = main.querySelector('.Drawer__Container');
    const container = document.createElement('div');
    container.className = 'Drawer__Container';
    container.innerHTML = MOUNT_HTML;

    if (after && after.parentNode === main) {
      main.insertBefore(container, after.nextSibling);
    } else {
      main.appendChild(container);
    }

    wrap = container.querySelector('#' + WRAP_ID);
    log('Recreated recos mount');
    return wrap;
  }

  // ---------------- card ----------------
  const cardHTML = (p) => {
    const v = firstVariant(p); if (!v || !v.id) return '';
    const img = pickImg(p);
    const href = productUrl(p);
    const price = (v.price ?? p.price);
    const compare = (v.compare_at_price ?? p.compare_at_price);
    const hasMany = Array.isArray(p?.variants) && p.variants.length > 1;

    const priceHTML = `
      <div class="dd-rec__price">
        <span>${money(price)}</span>
        ${compare && Number(compare) > Number(price) ? `<s>${money(compare)}</s>` : ''}
      </div>
    `;

    const ctaHTML = hasMany
      ? `<a class="dd-rec__btn" href="${href}">ADD</a>`
      : `<button class="dd-rec__btn" data-vid="${String(v.id)}" ${v.available === false ? 'disabled' : ''}>
           ${v.available === false ? 'Sold out' : 'Add'}
         </button>`;

    return `
      <article class="dd-rec">
        <a class="dd-rec__media" href="${href}">
          ${img ? `<img src="${sized(img, 360)}" alt="${(p.title||'').replace(/"/g,'&quot;')}" loading="lazy">` : ''}
        </a>
        <div class="dd-rec__info">
          <a class="dd-rec__title" href="${href}">${p.title || 'View product'}</a>
          ${priceHTML}
          ${ctaHTML}
        </div>
      </article>`;
  };

  // ---------------- data ----------------
  const getJSON = async (url) => {
    try {
      const r = await fetch(url, { credentials: 'same-origin' });
      return await r.json();
    } catch (e) {
      log('JSON fail', url, e);
      return null;
    }
  };
  const getCart = () => getJSON('/cart.js');
  const getRecs = (pid) =>
    getJSON(`/recommendations/products.json?product_id=${pid}&limit=${RECO_LIMIT}`)
      .then(d => d?.products || []);
  const getFallback = (handle, limit = RECO_LIMIT) =>
    getJSON(`/collections/${handle}/products.json?limit=${limit}`)
      .then(d => d?.products || []);

  // ---------------- state ----------------
  let building = false;
  let wiredOnce = false;
  let debTimer = null;

  function debounceBuild(delay = 160, reason = '') {
    if (debTimer) clearTimeout(debTimer);
    debTimer = setTimeout(() => {
      debTimer = null;
      build({ force: true, reason });
    }, delay);
  }

  async function build({ force = false, reason = '' } = {}) {
    const wrap = document.getElementById(WRAP_ID) || ensureMount();
    if (!wrap) { log('No mount; skip build', reason); return; }
    const grid = wrap.querySelector(GRID_SELECTOR);
    if (!grid) { log('No grid; skip build'); return; }

    if (building && !force) return;
    building = true;

    // Reset visual state
    wrap.classList.remove('dd-rec--ready');
    wrap.classList.add('dd-rec--pending');
    wrap.style.display = ''; // ensure visible unless we decide to hide

    const statusEl = wrap.querySelector(STATUS_SEL);
    if (statusEl) statusEl.textContent = 'Loading…';

    let products = [];
    try {
      const cart = await getCart();
      const itemCount = Number(cart?.item_count || 0);
      const pid = cart?.items?.[0]?.product_id || cart?.items?.[0]?.product?.id;
      log('build reason:', reason, '| itemCount:', itemCount, '| pid:', pid);

      // If cart empty, hide and bail (no fallback)
      if (!itemCount) {
        wrap.style.display = 'none'; // fully hide the section
        grid.innerHTML = '';         // remove any old cards
        wrap.classList.remove('dd-rec--pending');
        building = false;
        return;
      }

      // With items, try Shopify recs
      products = pid ? await getRecs(pid) : [];

      // Top-up from fallback collection if needed (and allowed)
      if (TOP_UP_FROM_COLLECTION && products.length < RECO_LIMIT) {
        const handle = wrap.getAttribute('data-fallback-handle') || FALLBACK_COLLECTION;
        const need = RECO_LIMIT - products.length;
        const fb = await getFallback(handle, RECO_LIMIT * 2); // fetch extra to allow dedupe
        const merged = dedupeByHandleOrId([...products, ...fb]);
        products = merged.slice(0, RECO_LIMIT);
        log('topped up with collection:', handle, '-> final', products.length);
      }

    } catch (e) {
      log('build error', e);
    }

    if (!products.length) {
      // If somehow nothing, show nothing (don’t confuse users)
      wrap.style.display = 'none';
      grid.innerHTML = '';
      wrap.classList.remove('dd-rec--pending');
      building = false;
      return;
    }

    grid.innerHTML = products.map(cardHTML).join('');

    // 1-click add for single-variant products
    grid.addEventListener('click', async (e) => {
      const btn = e.target.closest('.dd-rec__btn'); if (!btn || btn.tagName !== 'BUTTON') return;
      const id = Number(btn.getAttribute('data-vid')); if (!id) return;
      btn.disabled = true; const old = btn.textContent; btn.textContent = 'Adding…';
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id, quantity: 1 }] })
        });
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        btn.textContent = 'Added!';
        setTimeout(() => { btn.disabled = false; btn.textContent = old; }, 900);
      } catch (err) {
        log('add error', err);
        btn.disabled = false; btn.textContent = 'Try again';
      }
    }, { once: true });

    wrap.classList.remove('dd-rec--pending');
    wrap.classList.add('dd-rec--ready');
    building = false;
    log('rendered cards');
  }

  // Expose manual trigger for debugging
  window.DD_RECOS = { build };

  // ---------------- observers & hooks ----------------

  // (1) Network hooks: rebuild after cart AJAX completes
  function installCartNetworkHooks() {
    const cartPaths = ['/cart/add.js', '/cart/change.js', '/cart/update.js', '/cart/clear.js'];

    // fetch hook
    const origFetch = window.fetch;
    window.fetch = async function(...args) {
      const url = (args[0] && args[0].url) || args[0];
      const isCart = typeof url === 'string' && cartPaths.some(p => url.includes(p));
      const res = await origFetch.apply(this, args);
      if (isCart) {
        res.clone().text().catch(()=>{}).finally(() => {
          // let theme finish its DOM swap, then rebuild
          setTimeout(() => debounceBuild(120, 'fetch-cart'), 60);
          setTimeout(() => debounceBuild(240, 'fetch-cart-late'), 240);
        });
      }
      return res;
    };

    // XHR hook (older themes sometimes use it)
    const OrigXHR = window.XMLHttpRequest;
    function WrappedXHR() {
      const xhr = new OrigXHR();
      let url = '';
      const _open = xhr.open;
      xhr.open = function(method, u, ...rest) { url = String(u || ''); return _open.call(xhr, method, u, ...rest); };
      xhr.addEventListener('loadend', () => {
        if (cartPaths.some(p => url.includes(p))) {
          setTimeout(() => debounceBuild(120, 'xhr-cart'), 60);
          setTimeout(() => debounceBuild(260, 'xhr-cart-late'), 260);
        }
      });
      return xhr;
    }
    window.XMLHttpRequest = WrappedXHR;
  }

  // (2) Drawer open/close observer
  function observeDrawer() {
    const drawer = document.getElementById('sidebar-cart');
    if (!drawer) return;
    new MutationObserver(() => {
      const isOpen = drawer.getAttribute('aria-hidden') === 'false';
      if (isOpen) debounceBuild(140, 'drawer-open');
    }).observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
  }

  // (3) DOM subtree observer: if our mount is removed, recreate + rebuild
  function observeDrawerMain() {
    const main = document.querySelector('#sidebar-cart .Drawer__Main');
    if (!main) return;
    new MutationObserver(() => {
      if (!document.getElementById(WRAP_ID)) {
        ensureMount();
        debounceBuild(120, 'dom-swap');
      }
    }).observe(main, { childList: true, subtree: true });
  }

  // (4) Capture remove / qty clicks before theme handles them
  function wireUserActions() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-action="remove-item"]')) {
        setTimeout(() => debounceBuild(120, 'remove-click'), 40);
        setTimeout(() => debounceBuild(300, 'remove-click-late'), 300);
      }
      if (e.target.closest('[data-action="update-item-quantity"]')) {
        setTimeout(() => debounceBuild(140, 'qty-click'), 60);
        setTimeout(() => debounceBuild(320, 'qty-click-late'), 320);
      }
    }, true);

    document.addEventListener('change', (e) => {
      if (e.target && e.target.classList && e.target.classList.contains('QuantitySelector__CurrentQuantity')) {
        setTimeout(() => debounceBuild(160, 'qty-input'), 60);
        setTimeout(() => debounceBuild(360, 'qty-input-late'), 360);
      }
    }, true);
  }

  // One-time wiring
  function wireOnce() {
    if (wiredOnce) return;
    wiredOnce = true;
    installCartNetworkHooks();
    observeDrawer();
    observeDrawerMain();
    wireUserActions();
    document.addEventListener('DOMContentLoaded', () => debounceBuild(150, 'domready'));
    window.addEventListener('load', () => debounceBuild(220, 'load'));
  }

  wireOnce();
})();
